import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { TableRowData, TableSize } from "../types"
import React from "react"
import { classNames } from "../../../../utils/classNames"
import { Row } from "@tanstack/react-table"
import { VirtualItem, Virtualizer } from "@tanstack/react-virtual"
import { getOuterColumnPaddingClasses } from "../styleUtils"

type RowProps<TRowData> = {
    row: Row<TRowData>
    activeRowId?: Maybe<string>
    rowsHighlighting?: boolean
    size: TableSize
    bordered?: boolean
    rowClassName?: string
    onRowClick?: (data: TRowData) => void
    onRowClassName?: (data: TRowData, row: Row<TRowData>) => Maybe<string>
    children: React.ReactNode
    virtualized?: boolean
    virtualRow?: VirtualItem
    rowVirtualizer?: Virtualizer<HTMLDivElement, HTMLTableRowElement>
}

export const TableBodyRow = <TRowData extends TableRowData>(props: RowProps<TRowData>) => {
    // If virtualized - render the table body with virtual rows
    if (props.virtualized && props.virtualRow && props.rowVirtualizer) {
        return (
            <TableBodyRowVirtualized {...props} virtualRow={props.virtualRow} rowVirtualizer={props.rowVirtualizer} />
        )
    }

    return (
        <tr
            key={props.row.id}
            onClick={() => props.onRowClick && props.onRowClick(props.row.original)}
            className={classNames(
                classes.row,
                getOuterColumnPaddingClasses(props.size).body,
                (!!props.onRowClick || props.rowsHighlighting) && classes.highlightRow,
                !!props.onRowClick && classes.clickableRow,
                props.activeRowId != null && props.activeRowId === props.row.original.id && classes.activeRow,
                props.bordered && classes.bordered,
                props.rowClassName,
                props.onRowClassName && props.onRowClassName(props.row.original, props.row)
            )}>
            {props.children}
        </tr>
    )
}

const TableBodyRowVirtualized = <TRowData extends TableRowData>(props: Omit<RowProps<TRowData>, "virtualized">) => {
    return (
        <tr
            key={props.row.id}
            data-index={props.virtualRow?.index} //needed for dynamic row height measurement
            ref={node => props.rowVirtualizer?.measureElement(node)} //measure dynamic row height
            onClick={() => props.onRowClick && props.onRowClick(props.row.original)}
            className={classNames(
                classes.row,
                classes.rowVirtualized,
                getOuterColumnPaddingClasses(props.size).body,
                (!!props.onRowClick || props.rowsHighlighting) && classes.highlightRow,
                !!props.onRowClick && classes.clickableRow,
                props.activeRowId != null && props.activeRowId === props.row.original.id && classes.activeRow,
                props.bordered && classes.bordered,
                props.rowClassName,
                props.onRowClassName && props.onRowClassName(props.row.original, props.row)
            )}
            style={{
                transform: `translateY(${props.virtualRow?.start}px)`, //this should always be a `style` as it changes on scroll
            }}>
            {props.children}
        </tr>
    )
}

const classes = {
    row: "group/table-row",
    rowVirtualized: "flex absolute w-full",
    highlightRow: "hover:bg-[#EBF3EA]",
    clickableRow: "cursor-pointer",
    activeRow: "bg-primary-color/5",
    bordered: "[&>td]:border-r [&>td]:border-gray-200/80 [&>td:last-child]:border-r-0",
}
